// app/providers.tsx
"use client";

import { environmentFromHostname } from "@/lib/analytics/environment-from-hostname";
import React, { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { onFCP, onINP, onTTFB, type Metric } from "web-vitals";
import { onLCP, type LCPMetricWithAttribution } from "web-vitals/attribution";

import posthog from "posthog-js";
import { markPostHogReady } from "@/lib/analytics/capture-buffer";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { withEventFeature } from "@/lib/analytics/event-features";
import { captureProductClientEvent } from "@/lib/analytics/product-client-event";
import { isAnalyticsPaused } from "@/lib/analytics/analytics-pause";
import { deviceProperties } from "@/lib/analytics/device-properties";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { filterBrowserNetworkNoise } from "@/lib/posthog-exception-filter";
import { routeHydrationMismatch } from "@/lib/hydration-mismatch-diagnostics";
import {
  installServerActionResponseObserver,
  withServerActionResponse,
} from "@/lib/server-action-response-observer";
import { sweepLegacyCookies } from "@/lib/legacy-cookie-sweep";
import { showPendingHeaderOverflow } from "@/lib/header-overflow-recovery";
import {
  dropPublicLinkPageEvent,
  isPublicArtifactLinkPath,
} from "@/lib/public-artifact-link";
import { isSensitiveAuthLocation, redactSensitiveAuthEvent } from "@/lib/sensitive-auth-path";

// BRE-8443: at module load, not in the init effect: React runs child effects
// first, so actions fired on mount would start before an effect-time install.
installServerActionResponseObserver();

// Web-vitals "needs improvement" / "poor" thresholds per
// https://web.dev/articles/vitals — anything above the per-metric threshold
// is captured as `page.slow_load` so dashboards can alert on user-facing
// regressions without drowning in healthy samples.
const SLOW_THRESHOLDS_MS: Record<string, number> = {
  LCP: 2500,
  FCP: 1800,
  INP: 200,
  TTFB: 800,
};

/**
 * BRE-4346: derive a canonical `environment` super-property from the
 * window hostname so every event PostHog ingests carries a clean
 * production / staging / development split. Dashboards can then filter
 * on a single property instead of branching on `$host`. Mapped to the
 * SST stages so it matches what the server-side helper computes from
 * `SST_STAGE` / `NODE_ENV`.
 */
function deriveEnvironmentFromHostname(): "production" | "staging" | "development" {
  if (typeof window === "undefined") return "development";
  return environmentFromHostname(window.location.hostname);
}

export function PostHogProvider({
  posthogKey,
  children
}: {
  posthogKey: string;
  children: React.ReactNode;
}) {
  // BRE-8058: a public link's URL is its bearer token, so analytics never
  // start on its page; they start on the first other page the tab reaches
  // (a teammate redirected to the artifact in the app).
  // BRE-9668: the same holds for login, consent and OAuth callback pages, and
  // any URL carrying an auth param; `pathname` re-runs the check on navigation.
  const pathname = usePathname() ?? "";
  const onPublicLinkPage = isPublicArtifactLinkPath(pathname);
  const initializedRef = useRef(false);
  // BRE-9699: the too-many-cookies toast an RSC navigation's 413 left for this page.
  useEffect(() => showPendingHeaderOverflow(), []);
  useEffect(() => {
    // An empty key means analytics are off (the local stack, BRE-7831). Return
    // before `finally` would replay buffered captures into an SDK that never
    // loaded; the buffer's own cap bounds what waits.
    if (!posthogKey || onPublicLinkPage || initializedRef.current) return;
    if (isSensitiveAuthLocation(window.location.href)) return;
    initializedRef.current = true;
    try {
      posthog.init(posthogKey as string, {
        // BRE-5550: prefer the same-origin relay path (a CloudFront behavior
        // on the app's own distribution — see infra/web.ts POSTHOG_RELAY_PREFIX,
        // the single source of truth for the prefix). p.brev.io is PostHog's
        // managed proxy behind a CNAME to proxyhog.com, which CNAME-uncloaking
        // blockers (corporate DNS filters, uBlock/AdGuard) resolve and block —
        // 22.5% of RecordPoint's active users were dark. Same-origin traffic is
        // indistinguishable from app traffic at the DNS layer. The env var is
        // absent in local dev (no CloudFront), which falls back to the managed
        // proxy — that fallback is also the one-line rollback path.
        api_host: process.env.NEXT_PUBLIC_POSTHOG_RELAY_PREFIX
          ? `/${process.env.NEXT_PUBLIC_POSTHOG_RELAY_PREFIX}`
          : "https://p.brev.io",
        ui_host: "https://us.posthog.com",
        person_profiles: "identified_only",
        // BRE-5496: "history_change" makes posthog-js capture the initial
        // hard-load $pageview during init AND every SPA navigation (pushState /
        // replaceState / popstate) natively. The previous manual capture ran in
        // a CHILD component's useEffect, which React fires BEFORE this parent
        // effect - so on every hard page load the first $pageview was captured
        // pre-init and silently dropped. Users who hard-loaded once and left
        // (the failing-entry cohort) showed zero pageviews even though a page
        // rendered. Do not switch back to manual capture without solving that
        // ordering.
        capture_pageview: "history_change",
        autocapture: false,
        capture_pageleave: true,
        // BRE-9948: never `web_vitals_attribution`. It loads attributed CLS, whose
        // callback can retain detached DOM across SPA navigations. `page.slow_load`
        // carries the LCP phases instead.
        capture_performance: true,
        defaults: "2026-01-30",
        // BRE-5369: localStorage, NOT the default "localStorage+cookie".
        // Every cookie on this origin is sent on every request, including
        // every Server Action, and competes for the edge's total-header
        // budget. A signed-in session was measured sitting ~1.8 KB from a
        // 413; the `ph_..._posthog` cookie alone is ~442 B of that. Nothing
        // server-side reads it — only this SDK does — so persisting to
        // localStorage costs us nothing and buys back a quarter of the
        // remaining margin. Do not restore cookie persistence without
        // re-measuring headroom (see edge-rejection-probe.ts).
        persistence: "localStorage",
        // Drop un-actionable browser network-noise `$exception` events
        // (Safari "Load failed" / Chrome "Failed to fetch" / AbortError with
        // no stack) before they reach Error Tracking. These are in-flight
        // fetch rejections from navigation/connectivity blips, not logic
        // defects — see posthog-exception-filter.ts.
        // BRE-8058: and anything recorded on a public link page.
        // BRE-8443: and stamp the failed reply onto Next's opaque E394 events.
        // BRE-9118: and fingerprint stackless hydration errors, unpaged on auth pages.
        // BRE-9668: and anything on an auth page, and auth tokens in any URL.
        // BRE-9846: and everything, replay chunks included, while analytics are paused.
        before_send: (event) =>
          isAnalyticsPaused()
            ? null
            : redactSensitiveAuthEvent(
                dropPublicLinkPageEvent(
                  withServerActionResponse(filterBrowserNetworkNoise(routeHydrationMismatch(event))),
                ),
              ),
        disable_session_recording: isAnalyticsPaused(),
        // BRE-8058: no custom `maskCapturedNetworkRequestFn` for replay's network
        // log: setting one turns off posthog-js's default body scrubbing, and
        // its built-in path filter already drops `/s/` requests.
      });
      // BRE-4346: stamp environment as a super-property so every
      // capture (including the new route.interactive event) carries it.
      // Leave `$host` alone for backward-compat with existing dashboards.
      posthog.register({
        environment: deriveEnvironmentFromHostname(),
        ...deviceProperties(),
      });

    } catch (error) {
      console.warn("PostHog initialization failed:", error);
    } finally {
      // BRE-5538: replay captures that happened before init.
      //
      // `posthog.capture` is a no-op until `__loaded` is true, and it does not
      // throw — so pre-init events were lost with no signal at any layer. This
      // effect is a PARENT effect, and React runs child effects first, so any
      // descendant that captures during mount is guaranteed to lose that race.
      // Exactly how BRE-5496's dropped `$pageview` happened.
      //
      // Inside the try (after `register`) would be the obvious placement, so
      // that replayed events pick up the `environment` super-property. It is in
      // `finally` instead: if `init` throws, nothing would ever mark the buffer
      // ready and every capture for the rest of the page would queue silently
      // — turning a PostHog outage into an unbounded client-side buffer. On
      // that path the replay no-ops exactly as it did before this change, so
      // the failure mode is unchanged rather than made worse.
      //
      // The `environment` cost is real but small: it applies only to events
      // captured before init on a page where init SUCCEEDED, since `register`
      // runs immediately before this on the happy path.
      markPostHogReady((name, props, options) =>
        // The buffer holds names from every catalog, so stamp by name here.
        // eslint-disable-next-line no-restricted-properties -- replays the pre-init buffer
        posthog.capture(name, withEventFeature(name, props), options),
      );
    }

    // BRE-5369 follow-up: run AFTER init, so posthog-js has already carried
    // its state into localStorage — the sweep refuses to delete the cookie
    // until that twin exists. Deliberately outside the try above: an init
    // failure must not skip the cleanup, and the sweep never throws.
    sweepLegacyCookies(posthogKey);
  }, [posthogKey, onPublicLinkPage, pathname]);


  return (
    <PHProvider client={posthog}>
      <WebVitalsReporter />
      {children}
    </PHProvider>
  );
}

function reportSlowWebVital(metric: Metric, phases?: Record<string, number>) {
  const threshold = SLOW_THRESHOLDS_MS[metric.name];
  if (threshold === undefined || metric.value <= threshold) return;

  try {
    captureProductClientEvent(ANALYTICS_EVENTS.PAGE_SLOW_LOAD, {
      metric_name: metric.name,
      metric_value: metric.value,
      metric_rating: metric.rating,
      metric_id: metric.id,
      threshold_ms: threshold,
      navigation_type: metric.navigationType,
      path: window.location.pathname,
      ...phases,
    });
  } catch {
    // capture must never throw
  }
}

function reportSlowLcp(metric: LCPMetricWithAttribution) {
  const { timeToFirstByte, resourceLoadDelay, resourceLoadDuration, elementRenderDelay } = metric.attribution;
  reportSlowWebVital(metric, {
    lcp_ttfb_ms: timeToFirstByte,
    lcp_resource_delay_ms: resourceLoadDelay,
    lcp_resource_duration_ms: resourceLoadDuration,
    lcp_render_delay_ms: elementRenderDelay,
  });
}

// BRE-9774, BRE-9828: web-vitals never removes its observers, so register once
// per page load. Not `next/web-vitals`: its bundled v3 adds a `visibilitychange`
// listener on every click or keypress and never removes it.
let webVitalsRegistered = false;

function registerWebVitalsOnce() {
  if (webVitalsRegistered) return;
  webVitalsRegistered = true;
  onLCP(reportSlowLcp);
  onFCP(reportSlowWebVital);
  onINP(reportSlowWebVital);
  onTTFB(reportSlowWebVital);
}

export function WebVitalsReporter() {
  // An effect, so registration never runs during server rendering.
  useEffect(registerWebVitalsOnce, []);
  return null;
}

